import React, { useState, useEffect } from "react";
import { getDevis, deleteDevis, getDevisById, sendDevisByEmail } from "./api";
import EditDevis from "./EditDevis";
import "./ListDevis.css";
import DevisPreview from "./DevisPreview";
import {Eye,MagnifyingGlass,FileMinus,FileDashed,FilePdf,FileDoc,EnvelopeSimple} from "@phosphor-icons/react";
import { saveAs } from "file-saver";
import { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType, BorderStyle, AlignmentType } from "docx";
import Alert from "./components/Alert";
import LoadingProgress from "./components/LoadingProgress";

function ListDevis() {
  const [devis, setDevis] = useState([]);
  const [filteredDevis, setFilteredDevis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [editingDevisId, setEditingDevisId] = useState(null);
  const [showMenu, setShowMenu] = useState(null);
  
  // États pour le chargement
  const [isLoadingPDF, setIsLoadingPDF] = useState(false);
  const [isLoadingWord, setIsLoadingWord] = useState(false);
  const [isLoadingEmail, setIsLoadingEmail] = useState(false);
  
  // États pour la recherche et filtres
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);

  const [previewDevis, setPreviewDevis] = useState(null);
  const [selectedDevis, setSelectedDevis] = useState([]);

  useEffect(() => {
    loadDevis();
  }, []);

  // Effet pour filtrer et trier les devis
  useEffect(() => {
    let filtered = devis;

    // Filtrage par recherche
    if (searchTerm.trim() !== "") {
      filtered = filtered.filter(devisItem =>
        devisItem.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        devisItem.nom_entreprise?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        devisItem.societe?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        devisItem.contact?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        devisItem.destinataire?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        devisItem.objet?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        devisItem.user_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrage par statut
    if (filterStatus !== "all") {
      const today = new Date();
      const thirtyDaysAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000));
      
      filtered = filtered.filter(devisItem => {
        const devisDate = new Date(devisItem.date);
        switch (filterStatus) {
          case "recent":
            return devisDate >= thirtyDaysAgo;
          case "old":
            return devisDate < thirtyDaysAgo;
          case "thisMonth":
            return devisDate.getMonth() === today.getMonth() && 
                   devisDate.getFullYear() === today.getFullYear();
          default:
            return true;
        }
      });
    }

    // Tri
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case "reference":
          aValue = a.reference || "";
          bValue = b.reference || "";
          break;
        case "client":
          aValue = a.nom_entreprise || a.societe || "";
          bValue = b.nom_entreprise || b.societe || "";
          break;
        case "date":
          aValue = new Date(a.date);
          bValue = new Date(b.date);
          break;
        case "pages":
          aValue = a.nombrePages || 0;
          bValue = b.nombrePages || 0;
          break;
        default:
          aValue = new Date(a.date);
          bValue = new Date(b.date);
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    setFilteredDevis(filtered);
    setCurrentPage(1); // Reset à la première page lors du filtrage
  }, [devis, searchTerm, filterStatus, sortBy, sortOrder]);

  const loadDevis = async () => {
    try {
      const token = localStorage.getItem("token");
      const devisData = await getDevis(token);
      setDevis(devisData);
      setLoading(false);
    } catch (error) {
      console.error("Erreur lors du chargement des devis:", error);
      setMessage("Erreur lors du chargement des devis");
      setLoading(false);
    }
  };

  const handleDeleteDevis = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce devis ?")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await deleteDevis(token, id);
      setMessage("Devis supprimé avec succès");
      
      // Fermer la preview et retourner à la liste
      closePreview();
      
      // Recharger la liste des devis
      loadDevis();
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      setMessage("Erreur lors de la suppression du devis");
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedDevis.length === 0) return;
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedDevis.length} devis ?`)) return;
    try {
      const token = localStorage.getItem("token");
      for (const id of selectedDevis) {
        await deleteDevis(token, id);
      }
      setSelectedDevis([]);
      setMessage(`${selectedDevis.length} devis supprimé(s) avec succès`);
      loadDevis();
    } catch (error) {
      console.error('Erreur lors de la suppression multiple:', error);
      setMessage("Erreur lors de la suppression des devis");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };



  const handleBackToList = () => {
    setEditingDevisId(null);
    loadDevis(); // Recharger la liste
  };

  // Fermer le menu si on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = () => setShowMenu(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleMenuClick = (devisId, e) => {
    e.stopPropagation();
    setShowMenu(showMenu === devisId ? null : devisId);
  };

  const handleViewDevis = async (devisId) => {
    try {
      const token = localStorage.getItem("token");
      const devisData = await getDevisById(token, devisId);
      setPreviewDevis(devisData);
    } catch (error) {
      console.error('Erreur lors du chargement du devis:', error);
      alert('Erreur lors du chargement du devis');
    }
  };
  const closePreview = () => setPreviewDevis(null);

  const handleGeneratePDF = async (devisId) => {
    try {
      setIsLoadingPDF(true);
      const token = localStorage.getItem("token");
      
      // Charger les données complètes du devis depuis l'API
      const devisData = await getDevisById(token, devisId);
      if (!devisData) {
        setMessage("Impossible de charger les données du devis");
        return;
      }

      const html2pdf = (await import('html2pdf.js')).default;

      // Générer le HTML pour le PDF
      const pdfElement = generatePDFHTML(devisData);
      pdfElement.style.position = 'absolute';
      pdfElement.style.left = '-9999px';
      pdfElement.style.top = '0';
      document.body.appendChild(pdfElement);

      const elementHeight = pdfElement.scrollHeight; // px
      const A4_HEIGHT_PX = 1123; // ~297mm @ 96dpi
      let pageCount = Math.ceil(elementHeight / A4_HEIGHT_PX);
      if (pageCount < 1) pageCount = 1;

      // Nettoyer l'élément temporaire
      document.body.removeChild(pdfElement);

      // Régénérer le HTML final avec le bon nombre de pages
      const updatedPdfElement = generatePDFHTML(devisData, pageCount);
      updatedPdfElement.style.fontFamily = 'Times New Roman, serif';
      updatedPdfElement.style.fontSize = '14px';
      updatedPdfElement.style.color = '#111';
      updatedPdfElement.style.margin = '20px';
      updatedPdfElement.style.backgroundColor = '#ffffff';

      const opt = {
        margin: 10,
        filename: `devis_${devisData.contact || 'document'}_${devisData.nom_entreprise}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, allowTaint: true, backgroundColor: '#ffffff' },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      await html2pdf().set(opt).from(updatedPdfElement).save();
      setMessage(`PDF généré avec succès ! Nombre de pages: ${pageCount}`);
      
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      setMessage("Erreur lors de la génération du PDF. Vérifiez que html2pdf.js est installé.");
    } finally {
      setIsLoadingPDF(false);
    }
  };

  // Fonction pour envoyer le devis par mail
  const handleSendEmail = async (devisId) => {
    try {
      setIsLoadingEmail(true);
      const token = localStorage.getItem("token");
      
      // Appeler l'API pour envoyer le devis par mail
      await sendDevisByEmail(token, devisId);
      
      setMessage("Devis envoyé par email avec succès !");
      
      // Rafraîchir la liste des devis pour voir le nouveau statut
      await loadDevis();
      
      // Fermer le menu
      setShowMenu(null);
      
    } catch (error) {
      console.error('Erreur lors de l\'envoi du devis par email:', error);
      setMessage(`Erreur lors de l'envoi de l'email: ${error.message}`);
    } finally {
      setIsLoadingEmail(false);
    }
  };

  // Fonction pour générer le HTML du PDF (adaptée de CreateDevis.js)
  const generatePDFHTML = (devisData, pageCount = 1) => {
    const brandBlue = "0B3B7A";

    function createCell(content, options = {}) {
      const td = document.createElement("td");
      td.innerText = content || "";
      const setImportant = (el, prop, value) => el.style.setProperty(prop, value, 'important');
      if (options.bold) setImportant(td, 'font-weight', '700');
      if (options.color) setImportant(td, 'color', `#${options.color}`);
      else setImportant(td, 'color', '#111');
      if (options.bgColor) setImportant(td, 'background-color', `#${options.bgColor}`);
      setImportant(td, 'text-align', options.textAlign || 'left');
      if (options.fontSize) setImportant(td, 'font-size', options.fontSize + 'px');
      if (options.colSpan) td.colSpan = options.colSpan;
      if (options.rowSpan) td.rowSpan = options.rowSpan;
      if (options.width) setImportant(td, 'width', typeof options.width === 'number' ? `${options.width}px` : options.width);
      if (options.className) td.className = options.className;
      setImportant(td, 'border', '1px solid black');
      setImportant(td, 'padding', '8px');
      setImportant(td, 'vertical-align', 'middle');
      setImportant(td, 'line-height', '22px');
      setImportant(td, 'background-clip', 'padding-box');
      setImportant(td, 'display', 'table-cell');
      setImportant(td, 'white-space', 'normal');
      setImportant(td, 'word-break', 'break-word');
      return td;
    }

    function createTable(data, className = "", colWidths = []) {
      const table = document.createElement("table");
      table.className = className;
      const setImportant = (el, prop, value) => el.style.setProperty(prop, value, 'important');
      setImportant(table, 'width', '100%');
      setImportant(table, 'border-collapse', 'separate');
      setImportant(table, 'border-spacing', '0');
      setImportant(table, 'margin-bottom', '20px');
      setImportant(table, 'font-family', 'Times New Roman, serif');
      setImportant(table, 'font-size', '16px');
      setImportant(table, 'background-color', '#ffffff');
      setImportant(table, 'display', 'table');

      if (Array.isArray(colWidths) && colWidths.length > 0) {
        const colgroup = document.createElement('colgroup');
        colWidths.forEach(w => {
          const col = document.createElement('col');
          col.style.setProperty('width', typeof w === 'number' ? `${w}%` : w, 'important');
          colgroup.appendChild(col);
        });
        table.appendChild(colgroup);
      }
      data.forEach(rowData => {
        const tr = document.createElement("tr");
        setImportant(tr, 'display', 'table-row');
        rowData.forEach(cellData => { if (cellData) tr.appendChild(createCell(cellData.text, cellData)); });
        table.appendChild(tr);
      });
      return table;
    }

    function createParagraph(text, options = {}) {
      const p = document.createElement("p");
      p.innerText = text;
      if (options.marginTop) p.style.marginTop = options.marginTop + "px";
      if (options.marginBottom) p.style.marginBottom = options.marginBottom + "px";
      if (options.marginLeft) p.style.marginLeft = options.marginLeft + "px";
      if (options.color) p.style.color = `#${options.color}`;
      if (options.italic) p.style.fontStyle = "italic";
      if (options.bold) p.style.fontWeight = "bold";
      if (options.fontSize) p.style.fontSize = options.fontSize + "px";
      return p;
    }

    const container = document.createElement('div');
    container.className = 'devis-container';
    container.style.padding = '20px';
    container.style.backgroundColor = '#ffffff';
    container.style.color = '#111';
    container.style.fontFamily = 'Times New Roman, serif';
    container.style.fontSize = '14px';

    // Reset CSS to avoid global overrides and decorative pseudo-elements
    const resetStyle = document.createElement('style');
    resetStyle.textContent = `
      .devis-container, .devis-container * { box-sizing: border-box !important; }
      .devis-container *::before, .devis-container *::after { content: none !important; }
      .devis-container table { border-collapse: collapse !important; border-spacing: 0 !important; background:#fff !important; width: 100% !important; }
      .devis-container td, .devis-container th { border: 1px solid #000 !important; padding: 8px !important; vertical-align: middle !important; background:#fff !important; }
      .devis-container h4 { margin: 25px 0 10px 0 !important; padding-bottom: 6px !important; border-bottom: 2px solid #0B3B7A !important; }
      .devis-container .offre { background:#0B3B7A !important; color:rgb(255, 255, 255) !important; font-weight:700 !important; text-align:center !important; }
      .devis-container .pdf-header-table { width:100% !important; }
      .devis-container .gr { font-weight:700 !important; color:#0B3B7A !important; }
      .devis-container .p1 { font-weight:700 !important; color:#0B3B7A !important; }
      .devis-container .header-band {  !important; padding: 0px !important; border-radius: 4px !important; }
      .devis-container .devis-header td { height: 36px !important; }
    `;
    container.appendChild(resetStyle);

    const headerTable = createTable([
      // Ligne 1: Date | date | OFFRE DE PRIX (colSpan 2)
              [
          { text: 'Date', bold: true, fontSize: 14 },
          { text: devisData.date ? devisData.date.split('T')[0] : new Date().toISOString().split('T')[0], fontSize: 14 },
          { text: 'OFFRE DE PRIX', colSpan: 2, textAlign: 'center', bold: true, color: 'FFFFFF', fontSize: 16, className: 'offre'}
        ],
      // Ligne 2: Nombre de pages | valeur | N/Réf. | référence
      [
        { text: 'Nombre de pages (celle-ci comprise)', bold: true, fontSize: 14 },
        { text: String(pageCount), fontSize: 13 },
        { text: 'N/Réf.', bold: true, fontSize: 14, className: 'gr' },
        { text: devisData.reference || '', textAlign: 'center', fontSize: 14, className: 'p1' }
      ],
      // Ligne 3: Destinataire | valeur (colSpan 3)
      [
        { text: 'Destinataire', bold: true, fontSize: 14 },
        { text: devisData.destinataire || '', fontSize: 13, colSpan: 3 }
      ],
      // Ligne 4: Société | valeur (colSpan 3)
      [
        { text: 'Société', bold: true, fontSize: 14 },
        { text: devisData.societe || '', fontSize: 13, colSpan: 3 }
      ],
      // Ligne 5: Tél | valeur (colSpan 3)
      [
        { text: 'Tél', bold: true, fontSize: 14 },
        { text: devisData.telephone || '', fontSize: 13, colSpan: 3 }
      ],
      // Ligne 6: V/Réf. | valeur (colSpan 3)
      [
        { text: 'V/Réf.', bold: true, fontSize: 14 },
        { text: devisData.vRef || '', fontSize: 13, colSpan: 3 }
      ],
      // Ligne 7: Objet | valeur (colSpan 3)
      [
        { text: 'Objet', bold: true, fontSize: 14 },
        { text: devisData.objet || '', fontSize: 13, colSpan: 3 }
      ]
    ], 'pdf-header-table', [37, 21, 21, 21]);

    const headerBand = document.createElement('div');
    headerBand.className = 'header-band';
    headerBand.appendChild(headerTable);
    container.appendChild(headerBand);
    container.appendChild(createParagraph('Monsieur,', { marginTop: 20, marginBottom: 10 }));
    container.appendChild(createParagraph("Suite à votre demande, nous vous communiquons ci-après nos meilleures conditions pour la réalisation des prestations citées en objet.", { marginLeft: 20, marginBottom: 20 }));

    const generateSectionContent = (element) => {
      const getSectionType = (el) => { 
        if (el.type === 'section') return el.data.description; 
        if (el.parentSection) return el.parentSection.description; 
        return ''; 
      };
      const sectionType = getSectionType(element);

      if (sectionType.includes('Contrôle Qualité') || sectionType.includes('HSE')) {
        if (element.type === 'section') {
          const h4 = document.createElement('h4');
          h4.textContent = element.data.description;
          h4.style.color = `#${brandBlue}`;
          h4.style.fontWeight = 'bold';
          h4.style.fontSize = '18px';
          h4.style.borderBottom = `2px solid #${brandBlue}`;
          h4.style.marginTop = '25px';
          h4.style.marginBottom = '10px';
          container.appendChild(h4);
          const remarque = element.remarque;
          if (remarque) container.appendChild(createParagraph(`Remarque: ${remarque}`, { marginTop: 10, color: "d32f2f", italic: true }));
        }
        if (element.type === 'sousSection') {
          container.appendChild(createParagraph(`• ${element.data.description}`, { marginLeft: 20, marginTop: 10 }));
          const remarque = element.remarque;
          if (remarque) container.appendChild(createParagraph(`Remarque: ${remarque}`, { marginLeft: 20, color: "d32f2f", italic: true }));
        }
        if (element.type === 'item') {
          container.appendChild(createParagraph(`- ${element.data.description}`, { marginLeft: 40, marginTop: 5 }));
          const remarque = element.remarque;
          if (remarque) container.appendChild(createParagraph(`Remarque: ${remarque}`, { marginLeft: 40, color: "d32f2f", italic: true }));
        }
        if (element.type === 'sousItem') {
          const unite = element.unite ? ` .................................................... ${element.unite} µm` : '';
          container.appendChild(createParagraph(`-- ${element.data.description}${unite}`, { marginLeft: 60, marginTop: 3 }));
          const remarque = element.remarque;
          if (remarque) container.appendChild(createParagraph(`Remarque: ${remarque}`, { marginLeft: 60, color: "d32f2f", italic: true }));
        }
      } else if (sectionType.includes('Bordereau de prix') || sectionType.includes('Prix')) {
        if (element.type === 'section') {
          const h4 = document.createElement('h4');
          h4.textContent = element.data.description;
          h4.style.color = `#${brandBlue}`;
          h4.style.fontWeight = 'bold';
          h4.style.fontSize = '18px';
          h4.style.marginTop = '25px';
          h4.style.marginBottom = '10px';
          container.appendChild(h4);
          
          // Récupérer les sous-sections de prix depuis les éléments du devis
          const prixSousSections = devisData.elements?.filter(ss => 
            ss.type === 'sousSection' && 
            ss.parentSection?.description && 
            (ss.parentSection.description.includes('Bordereau de prix') || ss.parentSection.description.includes('Prix'))
          ) || [];
          
          if (prixSousSections.length > 0) {
            const tableRows = [[
              { text: 'Désignation', bgColor: brandBlue, color: 'FFFFFF', bold: true },
              { text: 'Unité', bgColor: brandBlue, color: 'FFFFFF', bold: true },
              { text: 'QT', bgColor: brandBlue, color: 'FFFFFF', bold: true },
              { text: 'PU DHS HT', bgColor: brandBlue, color: 'FFFFFF', bold: true },
              { text: 'PT DHS HT', bgColor: brandBlue, color: 'FFFFFF', bold: true }
            ]];
            
            prixSousSections.forEach(sousSection => {
              const priceInfo = sousSection.priceData || {};
              const qt = Number(priceInfo.qt || 0);
              const pu = Number(priceInfo.pu || 0);
              const total = qt * pu;
              tableRows.push([
                { text: sousSection.data?.description || sousSection.description || '' },
                { text: sousSection.unite || 'M²' },
                { text: qt.toFixed(2) },
                { text: pu.toFixed(2) },
                { text: total.toFixed(2), bold: true, color: brandBlue }
              ]);
            });
            const priceTable = createTable(tableRows, 'devis-table');
            container.appendChild(priceTable);
          }
          
          const remarque = element.remarque;
          if (remarque) container.appendChild(createParagraph(`(*) ${remarque}`, { marginTop: 10, marginLeft: 15, italic: true }));
        }
      } else if (sectionType.includes('Délai de réalisation') || sectionType.includes('Conditions de paiement') || sectionType.includes("Validité de l'offre")) {
        if (element.type === 'section') {
          const h4 = document.createElement('h4');
          h4.textContent = element.data.description;
          h4.style.color = `#${brandBlue}`;
          h4.style.fontWeight = 'bold';
          h4.style.fontSize = '18px';
          h4.style.marginTop = '25px';
          h4.style.marginBottom = '10px';
          container.appendChild(h4);
          const remarque = element.remarque;
          if (remarque) container.appendChild(createParagraph(`Remarque: ${remarque}`, { marginTop: 10, color: "d32f2f", italic: true }));
        }
        if (element.type === 'sousSection') {
          const value = element.value || '';
          container.appendChild(createParagraph(`• ${element.data.description} :${value};`, { marginLeft: 20, marginTop: 10, italic: true }));
          const remarque = element.remarque;
          if (remarque) container.appendChild(createParagraph(`Remarque: ${remarque}`, { marginLeft: 20, color: "d32f2f", italic: true }));
        }
      } else {
        if (element.type === 'section') {
          const h4 = document.createElement('h4');
          h4.textContent = element.data.description;
          h4.style.color = `#${brandBlue}`;
          h4.style.fontWeight = 'bold';
          h4.style.fontSize = '18px';
          h4.style.borderBottom = `2px solid #${brandBlue}`;
          h4.style.marginTop = '25px';
          h4.style.marginBottom = '10px';
          container.appendChild(h4);
          const remarque = element.remarque;
          if (remarque) container.appendChild(createParagraph(`Remarque: ${remarque}`, { marginTop: 10, color: "d32f2f", italic: true }));
        }
        if (element.type === 'sousSection') {
          const isChargeSection = element.data.description.includes('À notre charge') || element.data.description.includes('À votre charge');
          if (isChargeSection) { 
            container.appendChild(createParagraph(element.data.description, { marginTop: 40, bold: true})); 
          } else { 
            container.appendChild(createParagraph(`➤ ${element.data.description}`, { marginTop: 10, color: brandBlue})); 
          }
          const remarque = element.remarque;
          if (remarque) container.appendChild(createParagraph(`Remarque: ${remarque}`, { marginTop: 5, color: "d32f2f", italic: true }));
        }
        if (element.type === 'item') {
          container.appendChild(createParagraph(`● ${element.data.description}`, { marginLeft: 20, marginTop: 5 }));
          const remarque = element.remarque;
          if (remarque) container.appendChild(createParagraph(`Remarque: ${remarque}`, { marginLeft: 20, color: "d32f2f", italic: true }));
        }
        if (element.type === 'sousItem') {
          const unite = element.unite ? ` .............................................. ${element.unite} µm` : '';
          container.appendChild(createParagraph(`- ${element.data.description}${unite}`, { marginLeft: 40, marginTop: 3 }));
          const remarque = element.remarque;
          if (remarque) container.appendChild(createParagraph(`Remarque: ${remarque}`, { marginLeft: 40, color: "d32f2f", italic: true }));
        }
      }
    };

    // Générer le contenu des sections
    if (devisData.elements && Array.isArray(devisData.elements)) {
      devisData.elements.forEach(el => generateSectionContent(el));
    }
    
    container.appendChild(createParagraph("Nous restons à votre entière disposition pour tout renseignement complémentaire et vous prions d'agréer, Monsieur, nos meilleures salutations.", { marginTop: 20, marginBottom: 10 }));
    return container;
  };

  // Génération Word (.docx) avec docx
  const handleGenerateWord = async (devisId) => {
    try {
      setIsLoadingWord(true);
      const token = localStorage.getItem("token");
      
      // Charger les données complètes du devis depuis l'API
      const devisData = await getDevisById(token, devisId);
      if (!devisData) {
        setMessage("Impossible de charger les données du devis");
        return;
      }

      const brandBlue = "0B3B7A";
      
      // Debug: Afficher les données pour vérifier
      console.log("=== DEBUG GENERATION WORD ===");
      console.log("devisData:", devisData);
      console.log("elements:", devisData.elements);

      // Fonction helper pour créer des paragraphes avec label et valeur (non utilisée pour le moment)
      // const makeLabelLine = (label, value) => new Paragraph({
      //   children: [
      //     new TextRun({ text: `${label}: `, bold: true, color: "111111", size: 24 }),
      //     new TextRun({ text: value || "", color: "111111", size: 24 })
      //   ],
      //   spacing: { after: 120 }
      // });

      // Fonction utilitaire pour cellules
      const labelCell = (text, options = {}) =>
        new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text, bold: true, size: 22 })],
            })
          ],
          width: { size: options.width || 25, type: WidthType.PERCENTAGE },
          shading: options.shading || undefined,
        });

      const valueCell = (text, options = {}) => {
        const textValue = text !== null && text !== undefined ? String(text) : "";
        return new TableCell({
          children: [
            new Paragraph({
              children: [new TextRun({ text: textValue, size: 22 })],
            })
          ],
          width: { size: options.width || 25, type: WidthType.PERCENTAGE },
        });
      };

      // Fonction pour générer le contenu des sections
      const generateSectionContent = (element) => {
        const getSectionType = (element) => {
          if (element.type === 'section') {
            return element.data.description;
          }
          if (element.parentSection) {
            return element.parentSection.description;
          }
          return '';
        };

        const sectionType = getSectionType(element);
        const children = [];

        // Style pour "Contrôle Qualité - HSE"
        if (sectionType.includes('Contrôle Qualité') || sectionType.includes('HSE')) {
          if (element.type === 'section') {
            children.push(new Paragraph({
              children: [new TextRun({ text: element.data.description, color: brandBlue, bold: true, size: 28 })],
              spacing: { after: 240, before: 240 },
              border: {
                bottom: { space: 1, value: "single", size: 6 }
              },
            }));
            
            if (element.remarque) {
              children.push(new Paragraph({
                children: [new TextRun({ text: `Remarque: ${element.remarque}`, color: "d32f2f", italic: true })],
                spacing: { after: 120 }
              }));
            }
          }
          
          if (element.type === 'sousSection') {
            children.push(new Paragraph({
              children: [
                new TextRun({ text: "• " }),
                new TextRun({ text: element.data.description, bold: true })
              ],
              spacing: { after: 120 },
              indent: { left: 120 }
            }));
            
            if (element.remarque) {
              children.push(new Paragraph({
                children: [new TextRun({ text: `Remarque: ${element.remarque}`, color: "d32f2f", italic: true })],
                spacing: { after: 120 }
              }));
            }
          }
          
          if (element.type === 'item') {
            children.push(new Paragraph({
              children: [
                new TextRun({ text: "- " }),
                new TextRun({ text: element.data.description })
              ],
              spacing: { after: 120 },
              indent: { left: 720 }
            }));
            
            if (element.remarque) {
              children.push(new Paragraph({
                children: [new TextRun({ text: `Remarque: ${element.remarque}`, color: "d32f2f", italic: true })],
                spacing: { after: 120 },
                indent: { left: 720 }
              }));
            }
          }

          if (element.type === 'sousItem') {
            const unite = element.unite ? ` .................................................... ${element.unite} µm` : '';
            children.push(new Paragraph({
              children: [
                new TextRun({ text: "-- ", color: brandBlue }),
                new TextRun({ text: element.data.description + unite })
              ],
              spacing: { after: 120 },
              indent: { left: 1440 }
            }));
            
            if (element.remarque) {
              children.push(new Paragraph({
                children: [new TextRun({ text: `Remarque: ${element.remarque}`, color: "d32f2f", italic: true })],
                spacing: { after: 120 },
                indent: { left: 1440 }
              }));
            }
          }
        }
        
        // Style pour "Bordereau de prix"
        else if (sectionType.includes('Bordereau de prix') || sectionType.includes('Prix')) {
          if (element.type === 'section') {
            children.push(new Paragraph({
              children: [new TextRun({ text: element.data.description, color: brandBlue, bold: true, size: 28 })],
              spacing: { after: 240, before: 240 }
            }));
            
            // Tableau de prix avec le même style que l'en-tête
            const prixSousSections = devisData.elements?.filter(el => 
              el.type === 'sousSection' && el.parentSection?.description && 
              (el.parentSection.description.includes('Bordereau de prix') || el.parentSection.description.includes('Prix'))
            ) || [];

            if (prixSousSections.length > 0) {
              const tableRows = [
                // En-tête du tableau avec style bleu
                new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [new TextRun({ text: "Désignation", bold: true, color: "FFFFFF", size: 22 })]
                        })
                      ],
                      shading: { fill: brandBlue }
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [new TextRun({ text: "Unité", bold: true, color: "FFFFFF", size: 22 })]
                        })
                      ],
                      shading: { fill: brandBlue }
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [new TextRun({ text: "QT", bold: true, color: "FFFFFF", size: 22 })]
                        })
                      ],
                      shading: { fill: brandBlue }
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [new TextRun({ text: "PU DHS HT", bold: true, color: "FFFFFF", size: 22 })]
                        })
                      ],
                      shading: { fill: brandBlue }
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [new TextRun({ text: "PT DHS HT", bold: true, color: "FFFFFF", size: 22 })]
                        })
                      ],
                      shading: { fill: brandBlue }
                    })
                  ]
                })
              ];

              // Lignes de données
              prixSousSections.forEach(sousSection => {
                const priceInfo = sousSection.priceData || {};
                const qt = Number(priceInfo.qt || 0);
                const pu = Number(priceInfo.pu || 0);
                const total = qt * pu;
                
                tableRows.push(new TableRow({
                  children: [
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [new TextRun({ text: sousSection.data?.description || sousSection.description || "", size: 22 })]
                        })
                      ]
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [new TextRun({ text: sousSection.unite || 'M²', size: 22 })]
                        })
                      ]
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [new TextRun({ text: qt.toFixed(2), size: 22 })]
                        })
                      ]
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [new TextRun({ text: pu.toFixed(2), size: 22 })]
                        })
                      ]
                    }),
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [new TextRun({ text: total.toFixed(2), bold: true, color: brandBlue, size: 22 })]
                        })
                      ]
                    })
                  ]
                }));
              });

              children.push(new Table({
                rows: tableRows,
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: {
                  top: { style: BorderStyle.SINGLE, size: 1 },
                  bottom: { style: BorderStyle.SINGLE, size: 1 },
                  left: { style: BorderStyle.SINGLE, size: 1 },
                  right: { style: BorderStyle.SINGLE, size: 1 },
                  insideHorizontal: { style: BorderStyle.SINGLE, size: 1 },
                  insideVertical: { style: BorderStyle.SINGLE, size: 1 },
                }
              }));
              
              // Afficher la remarque en bas du tableau
              if (element.remarque) {
                children.push(new Paragraph({
                  children: [new TextRun({ text: `(*) ${element.remarque}`, italic: true })],
                  spacing: { after: 120 },
                  indent: { left: 301 }
                }));
              }
            }
          }
        }
        
        // Style pour les sections spéciales
        else if (sectionType.includes('Délai de réalisation') || sectionType.includes('Conditions de paiement') || sectionType.includes('Validité de l\'offre')) {
          if (element.type === 'section') {
            children.push(new Paragraph({
              children: [new TextRun({ text: element.data.description, color: brandBlue, bold: true, size: 28 })],
              spacing: { after: 240, before: 240 }
            }));
            
            if (element.remarque) {
              children.push(new Paragraph({
                children: [new TextRun({ text: `Remarque: ${element.remarque}`, color: "d32f2f", italic: true })],
                spacing: { after: 120 }
              }));
            }
          }
          
          if (element.type === 'sousSection') {
            const value = element.value || "";
            children.push(new Paragraph({
              children: [
                new TextRun({ text: "• " }),
                new TextRun({ text: element.data.description, bold: true }),
                new TextRun({ text: `  ${value};`, color: brandBlue, bold: true })
              ],
              spacing: { after: 120 },
              indent: { left: 120 }
            }));
            
            if (element.remarque) {
              children.push(new Paragraph({
                children: [new TextRun({ text: `Remarque: ${element.remarque}`, color: "d32f2f", italic: true })],
                spacing: { after: 120 }
              }));
            }
          }
        }
        
        // Style par défaut pour "Etendue des travaux" et autres
        else {
          if (element.type === 'section') {
            children.push(new Paragraph({
              children: [new TextRun({ text: element.data.description, color: brandBlue, bold: true, size: 28 })],
              spacing: { after: 240, before: 240 },
              border: {
                bottom: { color: brandBlue, space: 1, size: 6, value: "single" }
              }
            }));
            
            if (element.remarque) {
              children.push(new Paragraph({
                children: [new TextRun({ text: `Remarque: ${element.remarque}`, color: "d32f2f", italic: true })],
                spacing: { after: 120 },
              }));
            }
          }
          
          if (element.type === 'sousSection') {
            const isChargeSection = element.data.description.includes('À notre charge') || element.data.description.includes('À votre charge');
            children.push(new Paragraph({
              children: [
                isChargeSection ? 
                  new TextRun({ text: element.data.description, bold: true }) :
                  new TextRun({ text: "➤ ", color: brandBlue }),
                !isChargeSection && new TextRun({ text: element.data.description, bold: true, color: brandBlue })
              ].filter(Boolean),
              spacing: { after: 120, before: 340 }
            }));
            
            if (element.remarque) {
              children.push(new Paragraph({
                children: [new TextRun({ text: `Remarque: ${element.remarque}`, color: "d32f2f", italic: true })],
                spacing: { after: 120 }
              }));
            }
          }
          
          if (element.type === 'item') {
            children.push(new Paragraph({
              children: [
                new TextRun({ text: "● ", color: brandBlue }),
                new TextRun({ text: element.data.description })
              ],
              spacing: { after: 120 },
              indent: { left: 720 }
            }));
            
            if (element.remarque) {
              children.push(new Paragraph({
                children: [new TextRun({ text: `Remarque: ${element.remarque}`, color: "d32f2f", italic: true })],
                spacing: { after: 120 },
                indent: { left: 720 }
              }));
            }
          }

          if (element.type === 'sousItem') {
            const unite = element.unite ? ` .............................................. ${element.unite} µm` : '';
            children.push(new Paragraph({
              children: [
                new TextRun({ text: "- " }),
                new TextRun({ text: element.data.description + unite })
              ],
              spacing: { after: 120 },
              indent: { left: 1440 }
            }));
            
            if (element.remarque) {
              children.push(new Paragraph({
                children: [new TextRun({ text: `Remarque: ${element.remarque}`, color: "d32f2f", italic: true })],
                spacing: { after: 120 },
                indent: { left: 1440 }
              }));
            }
          }
        }

        return children;
      };

      // Créer le document Word
      const doc = new Document({
        sections: [{
          properties: {
            page: {
              margin: {
                top: 1440,
                right: 1440,
                bottom: 1440,
                left: 1440
              }
            }
          },
          children: [
            // Tableau d'en-tête (identique à l'aperçu)
            (() => {
              const headerTable = new Table({
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: {
                  top: { style: BorderStyle.SINGLE, size: 1 },
                  bottom: { style: BorderStyle.SINGLE, size: 1 },
                  left: { style: BorderStyle.SINGLE, size: 1 },
                  right: { style: BorderStyle.SINGLE, size: 1 },
                  insideHorizontal: { style: BorderStyle.SINGLE, size: 1 },
                  insideVertical: { style: BorderStyle.SINGLE, size: 1 },
                },
                rows: [
                  // Ligne 1 : Date + Offre de prix (merge)
                  new TableRow({
                    children: [
                      labelCell("Date"),
                      valueCell(devisData.date),
                      new TableCell({
                        columnSpan: 2,
                        children: [
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: "OFFRE DE PRIX",
                                bold: true,
                                color: "FFFFFF",
                                size: 28,
                              }),
                            ],
                            alignment: AlignmentType.CENTER,
                          }),
                        ],
                        shading: { fill: brandBlue },
                      }),
                    ],
                  }),
                  // Ligne 2 : Nombre de pages + N/Réf
                  new TableRow({
                    children: [
                      labelCell("Nombre de pages (celle-ci comprise)"),
                      valueCell(devisData.nombrePages),
                      labelCell("N/Réf."),
                      new TableCell({
                        children: [
                          new Paragraph({
                            children: [
                              new TextRun({
                                text: devisData.reference || "",
                                bold: true,
                                color: brandBlue,
                                size: 22,
                              })
                            ],
                          }),
                        ],
                      }),
                    ],
                  }),
                  // Ligne 3 : Destinataire
                  new TableRow({
                    children: [
                      labelCell("Destinataire"),
                      valueCell(devisData.destinataire),
                    ],
                  }),
                  // Ligne 4 : Société
                  new TableRow({
                    children: [
                      labelCell("Société"),
                      valueCell(devisData.societe),
                    ],
                  }),
                  // Ligne 5 : Téléphone
                  new TableRow({
                    children: [
                      labelCell("Tél"),
                      valueCell(devisData.telephone),
                    ],
                  }),
                  // Ligne 6 : V/Réf.
                  new TableRow({
                    children: [labelCell("V/Réf."), valueCell(devisData.vRef)],
                  }),
                  // Ligne 7 : Objet (merge sur 3 colonnes)
                  new TableRow({
                    children: [
                      labelCell("Objet"),
                      new TableCell({
                        columnSpan: 3,
                        children: [
                          new Paragraph({
                            children: [
                              new TextRun({ 
                                text: devisData.objet || "", 
                                size: 22 
                              })
                            ],
                          }),
                        ],
                      }),
                    ],
                  }),
                ],
              });
              return headerTable;
            })(),

            // Salutation
            new Paragraph({ text: "Monsieur,", spacing: { after: 120, before: 240 } }),
            new Paragraph({ 
              text: "Suite à votre demande, nous vous communiquons ci-après nos meilleures conditions pour la réalisation des prestations citées en objet.",
              spacing: { after: 240 },
              indent: { left: 300 }
            }),

            // Contenu des sections
            ...(devisData.elements || []).flatMap(element => generateSectionContent(element)),

            // Note de fin
            new Paragraph({ 
              text: "Nous restons à votre entière disposition pour tout renseignement complémentaire et vous prions d'agréer, Monsieur, nos meilleures salutations.",
              spacing: { after: 240, before: 240 }
            })
          ]
        }]
      });

      // Générer le document (navigateur)
      const blob = await Packer.toBlob(doc);
      const fileName = `devis_${devisData.reference || 'document'}.docx`;
      saveAs(blob, fileName);
      
      console.log("=== FIN DEBUG ===");
      setMessage("Document Word généré avec succès !");
    } catch (err) {
      console.error('Erreur génération DOCX:', err);
      setMessage("Erreur lors de la génération du document Word.");
    } finally {
      setIsLoadingWord(false);
    }
  };

  // Fonction pour éditer un devis
  const handleEditDevis = (devisId) => {
    // Fermer la preview actuelle
    closePreview();
    
    // Définir le devis à éditer
    setEditingDevisId(devisId);
  };

  // Fonction pour dupliquer un devis (non utilisée pour le moment)
  /*
  const handleDuplicateDevis = async (devisId) => {
    try {
      const devisItem = devis.find(d => d.id === devisId);
      if (!devisItem) return;

      // Créer une copie du devis avec une nouvelle référence
      const duplicatedDevis = {
        ...devisItem,
        id: undefined, // L'ID sera généré par la base de données
        reference: generateNewReference(devisItem.reference),
        date: new Date().toISOString().split('T')[0], // Date actuelle
        elements: devisItem.elements ? JSON.parse(JSON.stringify(devisItem.elements)) : [],
        remarques: devisItem.remarques ? JSON.parse(JSON.stringify(devisItem.remarques)) : {},
        unites: devisItem.unites ? JSON.parse(JSON.stringify(devisItem.unites)) : {},
        sousSectionValues: devisItem.sousSectionValues ? JSON.parse(JSON.stringify(devisItem.sousSectionValues)) : {},
        priceData: devisItem.priceData ? JSON.parse(JSON.stringify(devisItem.priceData)) : {}
      };

      // Supprimer les propriétés qui ne doivent pas être dupliquées
      delete duplicatedDevis.id;
      delete duplicatedDevis.created_at;
      delete duplicatedDevis.updated_at;

      // Envoyer à l'API pour créer le nouveau devis
      const token = localStorage.getItem("token");
      const response = await fetch('/api/devis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(duplicatedDevis)
      });

      if (response.ok) {
        setMessage("Devis dupliqué avec succès");
        loadDevis(); // Recharger la liste
      } else {
        throw new Error('Erreur lors de la duplication');
      }
    } catch (error) {
      console.error('Erreur lors de la duplication:', error);
      setMessage("Erreur lors de la duplication du devis");
    }
  };
  */

  // Fonction pour générer une nouvelle référence (non utilisée pour le moment)
  // const generateNewReference = (originalReference) => {
  //   const baseRef = originalReference.replace(/-\d+$/, ''); // Enlever le numéro final s'il existe
  //   const timestamp = Date.now().toString().slice(-6); // 6 derniers chiffres du timestamp
  //   return `${baseRef}-${timestamp}`;
  // };

  // Fonction pour exporter un devis (non utilisée pour le moment)
  /*
  const handleExportDevis = (devisId) => {
    try {
      const devisItem = devis.find(d => d.id === devisId);
      if (!devisItem) return;

      // Préparer les données pour l'export
      const exportData = {
        reference: devisItem.reference,
        date: devisItem.date,
        client: {
          nom_entreprise: devisItem.nom_entreprise,
          societe: devisItem.societe,
          contact: devisItem.contact,
          destinataire: devisItem.destinataire,
          telephone: devisItem.telephone,
          email: devisItem.email,
          reference_client: devisItem.reference_client
        },
        objet: devisItem.objet,
        nombrePages: devisItem.nombrePages,
        elements: devisItem.elements || [],
        remarques: devisItem.remarques || {},
        unites: devisItem.unites || {},
        sousSectionValues: devisItem.sousSectionValues || {},
        priceData: devisItem.priceData || {},
        created_at: devisItem.created_at,
        updated_at: devisItem.updated_at
      };

      // Créer le fichier JSON
      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      // Créer le lien de téléchargement
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `devis_${devisItem.reference}_export.json`;
      
      // Déclencher le téléchargement
      document.body.appendChild(link);
      link.click();
      
      // Nettoyer
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setMessage("Devis exporté avec succès");
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      setMessage("Erreur lors de l'export du devis");
    }
  };
  */

  // Calculs pour la pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDevis = filteredDevis.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredDevis.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Helpers pour les colonnes demandées
  const getClientName = (item) => item.contact || item.destinataire || '-';
  const getCompanyName = (item) => item.nom_entreprise || item.societe || '-';
  const getCreatedDate = (item) => formatDate(item.date_creation || item.created_at || item.date);
  const getUpdatedDate = (item) => (item.date_modification || item.updated_at) ? formatDate(item.date_modification || item.updated_at) : '-';
  const getCreatorName = (item) => item.user_name || item.created_by || (item.user && (item.user.username || item.user.name)) || item.user_id || '-';
  
  // Fonction pour afficher le badge de statut
  const getStatusBadge = (item) => {
    const status = item.status || 'non envoyé';
    
    switch (status) {
      case 'envoyé':
        return <span className="status-badge status-sent">Envoyé</span>;
      case 'non envoyé':
      default:
        return <span className="status-badge status-not-sent">Non envoyé</span>;
    }
  };

  if (editingDevisId) {
    return <EditDevis devisId={editingDevisId} onBack={handleBackToList} />;
  }

  if (previewDevis) {
    return (
      <DevisPreview 
        devis={previewDevis} 
        onClose={closePreview}
        onGeneratePDF={handleGeneratePDF}
        onGenerateWord={handleGenerateWord}
        onEdit={handleEditDevis}
        onDelete={handleDeleteDevis}
      />
    );
  }

  if (loading) {
    return (
      <div className="list-devis-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Chargement des devis...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="list-devis-container">
      <div className="list-header">
        <h2>Liste des devis</h2>
        <div className="header-stats">
          <span className="stat-item">
            <strong>{filteredDevis.length}</strong> devis trouvé(s)
          </span>
          <span className="stat-item">
            <strong>{devis.length}</strong> total
          </span>
        </div>
      </div>
      
      <Alert 
        message={message} 
        onClose={() => setMessage("")}
      />

      {/* Barre de recherche et filtres */}
      <div className="search-filters-bar">
        <div className="search-section">
          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder="Rechercher un devis..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <span className="search-icon"><MagnifyingGlass size={20} color="#626060" weight="duotone" /></span>
          </div>
        </div>

        <div className="filters-section">
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">Tous les devis</option>
            <option value="recent">Récents (30 jours)</option>
            <option value="old">Anciens</option>
            <option value="thisMonth">Ce mois</option>
          </select>

          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="date">Date</option>
            <option value="reference">Référence</option>
            <option value="client">Client</option>
            <option value="pages">Pages</option>
          </select>

          <button 
            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            className="sort-order-btn"
          >
            {sortOrder === "asc" ? "↑" : "↓"}
          </button>
        </div>
      </div>

      {filteredDevis.length === 0 ? (
        <div className="no-devis">
          <div className="no-devis-icon">📄</div>
          <h3>Aucun devis trouvé</h3>
          <p>
            {searchTerm || filterStatus !== "all" 
              ? "Aucun devis ne correspond à vos critères de recherche." 
              : "Aucun devis n'a encore été créé. Créez votre premier devis !"
            }
          </p>
          {searchTerm && (
            <button 
              onClick={() => setSearchTerm("")}
              className="clear-search-btn"
            >
              Effacer la recherche
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="clients-table devis-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedDevis.length === filteredDevis.length && filteredDevis.length > 0}
                      onChange={(e)=>{
                        if (e.target.checked) { setSelectedDevis(filteredDevis.map(d=>d.id)); } else { setSelectedDevis([]); }
                      }}
                    />
                  </th>
                  <th>Nom du client</th>
                  <th>Nom de l’entreprise</th>
                  <th>Date de création</th>
                  <th>Nom du créateur</th>
                  <th>Date de modification</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentDevis.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedDevis.includes(item.id)}
                        onChange={(e)=>{
                          if (e.target.checked) setSelectedDevis([...selectedDevis, item.id]);
                          else setSelectedDevis(selectedDevis.filter(id=>id!==item.id));
                        }}
                      />
                    </td>
                    <td>{getClientName(item)}</td>
                    <td>{getCompanyName(item)}</td>
                    <td>{getCreatedDate(item)}</td>
                    <td>{getCreatorName(item) || item.user_id || '-'}</td>
                    <td>{getUpdatedDate(item)}</td>
                    <td>{getStatusBadge(item)}</td>
                    <td className="actions">
                      <button 
                        className="menu-btn"
                        onClick={(e) => handleMenuClick(item.id, e)}
                        aria-label="Actions"
                      >
                        ···
                      </button>
                      {showMenu === item.id && (
                        <div className="menu" onClick={(e) => e.stopPropagation()}>
                          <button style={{display: "flex", alignItems: "center", gap: "10px", justifyContent: "start", background: "none", border: "none", cursor: "pointer", width: "100%", textAlign: "left", padding: "8px"}} onClick={() => handleViewDevis(item.id)}><Eye size={15} color="#212121" weight="duotone" /> Voir</button>
                          <button style={{display: "flex", alignItems: "center", gap: "10px", justifyContent: "start", background: "none", border: "none", cursor: "pointer", width: "100%", textAlign: "left", padding: "8px"}} onClick={() => handleGeneratePDF(item.id)}><FilePdf size={15} color="#212121" weight="duotone" /> Générer PDF</button>
                          <button style={{display: "flex", alignItems: "center", gap: "10px", justifyContent: "start", background: "none", border: "none", cursor: "pointer", width: "100%", textAlign: "left", padding: "8px"}} onClick={() => handleSendEmail(item.id)}><EnvelopeSimple size={15} color="#212121" weight="duotone" /> Envoyer par mail</button>
                          <button style={{display: "flex", alignItems: "center", gap: "10px", justifyContent: "start", background: "none", border: "none", cursor: "pointer", width: "100%", textAlign: "left", padding: "8px"}} onClick={() => handleGenerateWord(item.id)}><FileDoc size={15} color="#212121" weight="duotone" /> Générer Word</button>
                          <button style={{display: "flex", alignItems: "center", gap: "10px", justifyContent: "start", background: "none", border: "none", cursor: "pointer", width: "100%", textAlign: "left", padding: "8px"}} onClick={() => handleEditDevis(item.id)}><FileDashed size={15} color="#212121" weight="duotone" /> Modifier</button>
                          <button style={{display: "flex", alignItems: "center", gap: "10px", justifyContent: "start", background: "none", border: "none", cursor: "pointer", width: "100%", textAlign: "left", padding: "8px", color: '#e74c3c'}} onClick={() => handleDeleteDevis(item.id)}><FileMinus size={15} color="#212121" weight="duotone" />Supprimer</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {selectedDevis.length > 0 && (
            <button 
              className="delete-selected"
              onClick={handleDeleteSelected}
              style={{display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'start'}}
            >
              <FileMinus size={15} color="#fff5f5" weight="duotone" />Supprimer {selectedDevis.length} devis sélectionné(s)
            </button>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                ← Précédent
              </button>
              
              <div className="page-numbers">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                  <button
                    key={number}
                    onClick={() => paginate(number)}
                    className={`page-btn ${currentPage === number ? 'active' : ''}`}
                  >
                    {number}
                  </button>
                ))}
              </div>
              
              <button 
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                Suivant →
              </button>
            </div>
          )}
        </>
      )}
      
      {/* Composants de progression pour le chargement */}
      <LoadingProgress 
        isVisible={isLoadingPDF}
        message="Génération du PDF en cours..."
        showProgress={true}
        color="warning"
      />
      
      <LoadingProgress 
        isVisible={isLoadingWord}
        message="Génération du document Word en cours..."
        showProgress={true}
        color="primary"
      />
      
      <LoadingProgress 
        isVisible={isLoadingEmail}
        message="Envoi de l'email en cours..."
        showProgress={false}
        color="success"
      />
    </div>
  );
}

export default ListDevis; 